"use client";

import { siteHref } from "../current";
import { useRouter } from "../navigation";
import { useState } from "react";
import { deleteService, saveService } from "../actions";
import type { ServiceRecord } from "../types";
import { FaqItemsEditor } from "./FaqEditor";
import { LanguageBar, type EditLang } from "./locale";
import { SaveBar, useSave } from "./SaveBar";
import { CARD, ConfirmButton, LinesEditor, PageTitle, StatusSwitch, TextArea, TextField } from "./ui";

const slugify = (t: string) =>
  t.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const textOf = (s: Partial<ServiceRecord> | undefined) => ({
  name: s?.name ?? "",
  summary: s?.summary ?? "",
  intro: s?.intro ?? "",
  includes: s?.includes ?? [""],
  forWho: s?.forWho ?? "",
  faqs: s?.faqs ?? [],
  metaTitle: s?.metaTitle ?? "",
  metaDescription: s?.metaDescription ?? "",
});
type Text = ReturnType<typeof textOf>;

/** Create or edit a service page (/services/<web address> and /ar/services/<web address>). Arabic fields left empty
 * show the English text on the Arabic page, so fill them all. */
export function ServiceEditor({ service }: { service: ServiceRecord | null }) {
  const router = useRouter();
  const isNew = !service;
  const [lang, setLang] = useState<EditLang>("en");
  const [form, setForm] = useState(() => ({ id: service?.id ?? "", status: service?.status ?? ("draft" as const), slug: service?.slug ?? "", ...textOf(service ?? undefined) }));
  const [ar, setAr] = useState(() => textOf(service?.ar));
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const { pending, notice, dirty, markDirty, run } = useSave();
  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => {
    markDirty();
    setForm((f) => ({ ...f, [field]: value, ...(field === "name" && !slugTouched ? { slug: slugify(String(value)) } : {}) }));
  };
  const setArField = <K extends keyof Text>(field: K, value: Text[K]) => {
    markDirty();
    setAr((a) => ({ ...a, [field]: value }));
  };
  // The fields of the language being edited.
  const text = lang === "en" ? form : ar;
  const setText = <K extends keyof Text>(field: K, value: Text[K]) => (lang === "en" ? set(field, value as (typeof form)[K]) : setArField(field, value));
  const arabicMissing = !ar.name.trim() || !ar.summary.trim() || !ar.intro.trim() || !ar.forWho.trim() || !ar.includes.some((x) => x.trim()) || !ar.metaDescription.trim();

  const save = () => run(() => saveService({ ...form, ar }), form.status === "published" ? "Saved and published." : "Saved as a draft (not on the website).", (r) => r.ok && isNew && r.id && router.replace(`/admin/services/${r.id}`));

  return (
    <div>
      <PageTitle title={isNew ? "New service" : "Edit service"}>
        {!isNew && form.status === "published" && (
          <>
            <a href={siteHref(`/services/${form.slug}`)} target="_blank" rel="noreferrer" className="py-2 text-sm text-teal underline underline-offset-4">
              View on website ↗
            </a>
            <a href={siteHref(`/ar/services/${form.slug}`)} target="_blank" rel="noreferrer" className="py-2 text-sm text-teal underline underline-offset-4">
              Arabic ↗
            </a>
          </>
        )}
      </PageTitle>
      <LanguageBar
        lang={lang}
        onChange={setLang}
        missing={{ ar: arabicMissing }}
        arabicNote="Arabic text for /ar/services. Any field left empty shows the English text there. The web address and status are shared: change them on the English tab."
      />
      <div key={lang} className="flex flex-col gap-6">
        <section className={`${CARD} flex flex-col gap-5`}>
          <TextField label="Name" required={lang === "en"} lang={lang} value={text.name} onChange={(v) => setText("name", v)} max={60} placeholder={lang === "ar" ? form.name : undefined} />
          {lang === "en" && (
            <TextField
              label="Web address"
              required
              value={form.slug}
              onChange={(v) => {
                setSlugTouched(true);
                set("slug", slugify(v));
              }}
              hint={<>The page&apos;s address: <strong>/services/{form.slug || "…"}</strong> (Arabic: <strong>/ar/services/{form.slug || "…"}</strong>)</>}
            />
          )}
          <TextArea label="One-line summary" required={lang === "en"} lang={lang} value={text.summary} onChange={(v) => setText("summary", v)} rows={2} hint="Shown on cards, in the home page list and the footer." />
          <TextArea label="Opening answer" required={lang === "en"} lang={lang} value={text.intro} onChange={(v) => setText("intro", v)} rows={5} hint="40–60 words: what the service is and who it is for." />
          <LinesEditor label={lang === "en" ? "What's included *" : "What's included"} lang={lang} value={text.includes} onChange={(v) => setText("includes", v)} addLabel="Add item" />
          <TextArea label="Who it's for" required={lang === "en"} lang={lang} value={text.forWho} onChange={(v) => setText("forWho", v)} rows={3} />
        </section>
        <section className={`${CARD} flex flex-col gap-5`}>
          <h2 className="text-xl font-bold">Common questions</h2>
          <FaqItemsEditor lang={lang} value={text.faqs} onChange={(v) => setText("faqs", v)} />
        </section>
        <section className={`${CARD} flex flex-col gap-5`}>
          <h2 className="text-xl font-bold">Google</h2>
          <TextField label="Title in Google" lang={lang} value={text.metaTitle} onChange={(v) => setText("metaTitle", v)} max={60} hint="Leave empty to use the name." />
          <TextArea label="Description in Google" required={lang === "en"} lang={lang} value={text.metaDescription} onChange={(v) => setText("metaDescription", v)} max={160} rows={3} hint="120–160 characters, written for people." />
        </section>
      </div>
      <SaveBar onSave={save} pending={pending} dirty={dirty} notice={notice}>
        <StatusSwitch value={form.status} onChange={(v) => set("status", v)} />
        {!isNew && <ConfirmButton question="Delete this service (both languages)?" onConfirm={() => run(() => deleteService(form.id), "Deleted.", (r) => r.ok && (router.replace("/admin/services"), router.refresh()))} />}
      </SaveBar>
    </div>
  );
}
